// Visualization3D Page - Router to dedicated checkpoint pages
import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { useAuthContext as useAuth } from '../contexts/AuthContext';
import { Visualization3DGuard } from '../lib/permissions';
import { Visualization3DCP1APage } from './Visualization3DCP1APage';
import { Visualization3DCP1BPage } from './Visualization3DCP1BPage';

export const Visualization3DPage: React.FC = () => {
  const { user } = useAuth();
  const [selectedCheckpoint, setSelectedCheckpoint] = useState<string>('cp-1a');
  const [showMobileLegend, setShowMobileLegend] = useState<boolean>(false);
  const [showMobileHeader, setShowMobileHeader] = useState<boolean>(true);
  const [headerTimeout, setHeaderTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isInteracting, setIsInteracting] = useState<boolean>(false);
  const [showControlButton, setShowControlButton] = useState<boolean>(false);

  // Handle checkpoint dropdown change
  const handleCheckpointChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newCheckpointId = event.target.value;
    setSelectedCheckpoint(newCheckpointId);
    setIsInteracting(true);
    
    // Reset interaction flag
    setTimeout(() => {
      setIsInteracting(false);
    }, 1000);
  };

  // Toggle mobile legend
  const toggleMobileLegend = () => {
    setShowMobileLegend(!showMobileLegend);
    setIsInteracting(true);
    
    // Reset interaction flag
    setTimeout(() => {
      setIsInteracting(false);
    }, 1000);
  };

  // Auto-hide mobile header after 7 seconds
  useEffect(() => {
    // Only auto-hide on mobile (when mobile header is visible and not interacting)
    const isMobileView = window.innerWidth < 1024;
    
    if (isMobileView && showMobileHeader && !isInteracting) {
      // Clear any existing timeout
      if (headerTimeout) {
        clearTimeout(headerTimeout);
      }
      
      // Set new timeout to hide header after 7 seconds
      const timeout = setTimeout(() => {
        setShowMobileHeader(false);
      }, 7000);
        
      setHeaderTimeout(timeout);
      
      // Cleanup
      return () => {
        if (timeout) {
          clearTimeout(timeout);
        }
      };
    }
  }, [showMobileHeader, isInteracting]);

  // Auto-hide the show control button after 3 seconds
  useEffect(() => {
    if (showControlButton && !showMobileHeader) {
      const timeout = setTimeout(() => {
        setShowControlButton(false);
      }, 3000);
      
      return () => clearTimeout(timeout);
    }
  }, [showControlButton, showMobileHeader]);

  // Show header when user interacts with it
  const handleMobileHeaderInteraction = () => {
    setIsInteracting(true);
    setShowMobileHeader(true);
    
    // Reset interaction flag after a delay
    setTimeout(() => {
      setIsInteracting(false);
    }, 1000);
  };

  // Toggle header visibility
  const toggleMobileHeader = () => {
    setShowMobileHeader(!showMobileHeader);
    setIsInteracting(true);
    
    // Reset interaction flag
    setTimeout(() => {
      setIsInteracting(false);
    }, 1000);
  };

  // Use modern permission guard for consistent access control
  const accessDeniedFallback = (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />
      <div className="flex items-center justify-center h-[calc(100vh-64px)]">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-gray-400">You don't have permission to access 3D View features.</p>
          <p className="text-gray-500 text-sm mt-2">Required permission: 3d_visualisation_access</p>
        </div>
      </div>
    </div>
  );

  // Render with modern permission guard
  return (
    <Visualization3DGuard fallback={accessDeniedFallback}>
      <div className="min-h-screen bg-gray-900 text-white">
        <Header />
        
        <div className="flex flex-col h-[calc(100vh-64px)] relative">
          {/* Desktop Header - Hidden on mobile */}
          <div className="hidden lg:block bg-gray-800 border-b border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-white">
                  3D View - {selectedCheckpoint === 'cp-1a' ? 'Checkpoint 1A' : 'Checkpoint 1B'}
                </h1>
              </div>
              
              {/* Desktop Legends Container - stacked vertically */}
              <div className="flex-1 flex flex-col items-center space-y-2">
                {/* Occupancy Legend */}
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-300 font-medium">Occupancy:</div>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-gray-400"></div>
                      <span className="text-xs text-gray-300">Zero</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-500"></div>
                      <span className="text-xs text-gray-300">Low</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-yellow-500"></div>
                      <span className="text-xs text-gray-300">Normal</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-amber-500"></div>
                      <span className="text-xs text-gray-300">Moderate</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-red-500"></div>
                      <span className="text-xs text-gray-300">High</span>
                    </div>
                  </div>
                </div>

                {/* Passenger Flow Legend */}
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-300 font-medium">Passenger Flow:</div>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span className="text-xs text-gray-300">Fast</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <span className="text-xs text-gray-300">Moderate</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <span className="text-xs text-gray-300">Slow</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex-1 flex justify-end items-center">
                {/* Desktop Checkpoint Dropdown */}
                <div className="flex items-center space-x-3">
                  <label htmlFor="checkpoint-select" className="text-sm font-medium text-gray-300">
                    Checkpoint:
                  </label>
                  <select
                    id="checkpoint-select"
                    value={selectedCheckpoint}
                    onChange={handleCheckpointChange}
                    className="bg-gray-700 text-white px-3 py-2 rounded-md border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="cp-1a">Checkpoint 1A (T1-1A)</option>
                    <option value="cp-1b">Checkpoint 1B (T1-1B)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Header - Absolute positioned to overlay */}
          <div className={`lg:hidden absolute top-0 left-0 right-0 z-20 transition-all duration-300 ${
            showMobileHeader ? '' : 'pointer-events-none'
          }`}>
            {/* Sliding header */}
            <div 
              className={`bg-gray-800 border-b border-gray-700 transition-all duration-300 ${
                showMobileHeader ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform -translate-y-full'
              }`}
              onClick={handleMobileHeaderInteraction}
            >
            {/* Title and Controls */}
            <div className="px-4 py-3 space-y-3">
              {/* Title Row */}
              <div className="flex items-center justify-between">
                <h1 className="text-lg font-bold text-white">
                  3D View
                </h1>
                <button
                  onClick={toggleMobileLegend}
                  className="text-gray-300 hover:text-white p-2 rounded-md"
                  aria-label="Toggle Legend"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </div>

              {/* Checkpoint Selector Row */}
              <div className="flex items-center space-x-3">
                <label htmlFor="checkpoint-select-mobile" className="text-sm font-medium text-gray-300">
                  Checkpoint:
                </label>
                <select
                  id="checkpoint-select-mobile"
                  value={selectedCheckpoint}
                  onChange={handleCheckpointChange}
                  className="flex-1 bg-gray-700 text-white px-3 py-2 rounded-md border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm"
                >
                  <option value="cp-1a">CP 1A (T1-1A)</option>
                  <option value="cp-1b">CP 1B (T1-1B)</option>
                </select>
              </div>

              {/* Mobile Legend Dropdown */}
              {showMobileLegend && (
                <div className="bg-gray-700 rounded-md p-3 space-y-3">
                  {/* Occupancy Legend */}
                  <div className="space-y-2">
                    <div className="text-sm text-gray-300 font-medium">Occupancy:</div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-gray-400"></div>
                        <span className="text-xs text-gray-300">Zero</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-green-500"></div>
                        <span className="text-xs text-gray-300">Low</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-yellow-500"></div>
                        <span className="text-xs text-gray-300">Normal</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-amber-500"></div>
                        <span className="text-xs text-gray-300">Moderate</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-red-500"></div>
                        <span className="text-xs text-gray-300">High</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Passenger Flow Legend */}
                  <div className="space-y-2">
                    <div className="text-sm text-gray-300 font-medium">Passenger Flow:</div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <span className="text-xs text-gray-300">Fast</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                        <span className="text-xs text-gray-300">Moderate</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        <span className="text-xs text-gray-300">Slow</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            </div>
          </div>

          {/* Render selected checkpoint - Full height on mobile */}
          <div className={`flex-1 overflow-auto lg:relative ${
            showMobileHeader ? 'absolute inset-x-0 top-16 bottom-0 z-10' : 'absolute inset-0 z-10'
          } lg:inset-auto lg:z-auto`}>
            {selectedCheckpoint === 'cp-1a' ? (
              <Visualization3DCP1APage />
            ) : (
              <Visualization3DCP1BPage />
            )}
          </div>
        </div>
        
        {/* Invisible hover zone at top to show controls button - positioned below main header */}
        {!showMobileHeader && (
          <div 
            className="lg:hidden fixed top-16 left-0 right-0 h-12 z-[99998]"
            onMouseEnter={() => setShowControlButton(true)}
            onMouseLeave={() => setShowControlButton(false)}
            onTouchStart={() => setShowControlButton(true)}
          />
        )}
        
        {/* Show Controls Button - Only visible on hover/touch */}
        {!showMobileHeader && showControlButton && (
          <button
            onClick={() => {
              toggleMobileHeader();
              setShowControlButton(false);
            }}
            onTouchStart={() => {
              toggleMobileHeader();
              setShowControlButton(false);
            }}
            className="fixed top-16 left-1/2 transform -translate-x-1/2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-5 py-2.5 rounded-full shadow-xl text-sm z-[99999] flex items-center space-x-2 lg:hidden animate-fade-in"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            <span>Show Controls</span>
          </button>
        )}
      </div>
    </Visualization3DGuard>
  );
};